import { FC } from "react";
import { TimelineConnector, TimelineContent, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Box, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../../../../../common/utils";
import ValueChip from "../../../../../../../../components/charts/MultiValueWidget/ValueChip";
import {
	EntityItemDTO,
	TripArcDTO,
	TripVehicleEnergyCarrierMixDTO,
} from "../../../../../../../../datasources/api-client/data-contracts";
import { getTransportTypeIcon } from "../../../../../tabs/Greenrouter/data";
import { ArcEntities } from "../ArcEntities";

interface Props {
	arc: TripArcDTO;
	visibleEntities?: string[];
	entitiesScrollRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Renders details about a single arc of the viewing trip
 * MUST be used inside a Timeline object
 * @param arc the arc to be displayed
 * @param visibleEntities
 * @returns
 */
export const ArcDetails: FC<Props> = ({ arc, visibleEntities = [], entitiesScrollRef }) => {
	const { t } = useTranslation("feature.results");

	const vehicleTopRowComponents = [];
	if (arc.vehicle.categoryDescription) vehicleTopRowComponents.push(arc.vehicle.categoryDescription);
	if (arc.vehicle.sizeDetailsDescription) vehicleTopRowComponents.push(arc.vehicle.sizeDetailsDescription);
	if (arc.vehicle.vehicleDetailsDescription) vehicleTopRowComponents.push(arc.vehicle.vehicleDetailsDescription);
	if (arc.vehicle.energyCarrierClassDescription)
		vehicleTopRowComponents.push(arc.vehicle.energyCarrierClassDescription);
	if (arc.vehicle.emissionStandardDescription) vehicleTopRowComponents.push(arc.vehicle.emissionStandardDescription);

	const sortedMix = arc.vehicle.energyCarrierMix
		.filter((m: TripVehicleEnergyCarrierMixDTO) => m.ecId > 0)
		.sort((a: TripVehicleEnergyCarrierMixDTO, b: TripVehicleEnergyCarrierMixDTO) => b.share.value - a.share.value);
	const vehicleBottomRowComponents: Array<string> = sortedMix.map(
		(m: TripVehicleEnergyCarrierMixDTO) => `${m.ecDescription}: ${m.share.value}${m.share.measure}`
	);

	// setting up heading for our ArcDetails
	const heading = (vehicleTopRowComponents.length > 0 || vehicleBottomRowComponents.length > 0) && (
		<Stack direction="column">
			<Stack direction="row" spacing={1} alignItems="center">
				{vehicleTopRowComponents.length > 0 && (
					<Typography variant="h6">{vehicleTopRowComponents.join(" - ")}</Typography>
				)}
				{arc.vehicle.customPrimaryData >= 1 && (
					<Chip size="small" color="secondary" label={t("tripDetails/primary")} />
				)}
			</Stack>

			{vehicleBottomRowComponents.length > 0 && (
				<Typography variant="caption">{vehicleBottomRowComponents.join(" - ")}</Typography>
			)}
		</Stack>
	);

	const icon = getTransportTypeIcon(arc.transportTypeId, { fontSize: 20 });

	const values = [
		{
			label: t(`tripDetails/distance`),
			value: formatNumber(arc.distance.value, "decimal", 2, 2),
			measure: arc.distance.measure,
			reqExpand: false,
		},
		{
			label: t(`arcDetails/weight`),
			value: formatNumber(arc.weight.value, "decimal", 2, 2),
			measure: arc.weight.measure,
			reqExpand: false,
		},
		{
			label: t("arcDetails/emissions ttw"),
			value: formatNumber(arc.tCo2eTtw.value, "decimal", 2, 2),
			measure: arc.tCo2eTtw.measure,
			reqExpand: false,
		},
		{
			label: t("arcDetails/emissions wtw"),
			value: formatNumber(arc.tCo2eWtw.value, "decimal", 2, 2),
			measure: arc.tCo2eTtw.measure,
			reqExpand: false,
		},
		{
			label: t("arcDetails/emission intensity") + " TtW",
			value: formatNumber(arc.tCo2eKmTtw.value, "decimal", 2, 2),
			measure: arc.tCo2eKmTtw.measure,
			reqExpand: false,
		},
		{
			label: t("arcDetails/emission intensity") + " WtW",
			value: formatNumber(arc.tCo2eKmWtw.value, "decimal", 2, 2),
			measure: arc.tCo2eKmWtw.measure,
			reqExpand: false,
		},
		{
			label: t("arcDetails/pmx"),
			value: formatNumber(arc.pmx.value, "decimal", 2, 2),
			measure: arc.pmx.measure,
			reqExpand: false,
		},
	];

	const entities = (arc.entities ?? []).filter((e: EntityItemDTO) => visibleEntities.includes(e.id));

	return (
		<>
			<TimelineItem>
				<TimelineSeparator>
					<TimelineConnector />
				</TimelineSeparator>
				<TimelineContent position="relative" left="0" padding="0 !important" minWidth="calc(100% - 28px)">
					<Paper
						elevation={3}
						sx={{
							padding: 2,
							marginLeft: "64px !important",
							marginBottom: 2,
						}}
					>
						<Paper
							sx={{
								position: "absolute",
								width: 32,
								height: 32,
								left: -16,
								zIndex: 2,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							{icon}
						</Paper>
						<Stack direction="column" spacing={1}>
							{heading}
							<Grid container spacing={2} justifyContent={"space-between"}>
								{values.map((displayableValue, index) => {
									return <ValueChip key={index} value={displayableValue} display={"block"} />;
								})}
							</Grid>
						</Stack>
					</Paper>
				</TimelineContent>
			</TimelineItem>

			<Box ref={entitiesScrollRef}>
				<ArcEntities entities={entities} />
			</Box>
		</>
	);
};

export default ArcDetails;
